import styled from '@emotion/styled';
import { rgba } from 'polished';
import { ReactNode } from 'react';
import * as React from 'react';

import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';
import { hoverOpacity } from '@/utility/theme/animation';

type ButtonProps = {
  children: ReactNode;
  bgColor: keyof typeof defaultTheme.background;
  size: keyof typeof defaultTheme.font.size;
  clicked: boolean;
  disabled?: boolean;
  outline?: boolean;
  width?: string;
  height?: string;
  type?: 'button' | 'reset' | 'submit' | undefined;
  onClick?: () => void;
};

const getColor = (props: ButtonProps) => {
  if (props.clicked) {
    if (props.outline) {
      if (
        (Object.values(defaultTheme.color.grayScale) as string[]).includes(
          defaultTheme.background[props.bgColor],
        )
      ) {
        return rgba(
          defaultTheme.background[props.bgColor],
          defaultTheme.button.opacity.disabled,
        );
      }
      return defaultTheme.background[props.bgColor];
    }
    return defaultTheme.text.white;
  }
  if (props.disabled) {
    return rgba(defaultTheme.text.default, 0.3);
  }
  if (props.bgColor === 'white') {
    return defaultTheme.text.default;
  }
  return defaultTheme.background[props.bgColor];
};

const getBgColor = (props: ButtonProps) => {
  if (props.outline) return defaultTheme.background.white;
  if (props.clicked) return defaultTheme.background[props.bgColor];
  return '';
};

const StyledButton = styled.button((props: ButtonProps) =>
  mq({
    width: props.width ? props.width : '100%',
    height: props.height ? props.height : '100%',
    color: getColor(props),
    textAlign: 'center',
    border: `${props.clicked ? 2 : 1}px solid ${
      props.clicked
        ? defaultTheme.background[props.bgColor]
        : defaultTheme.border.color.mediumLightGray
    }`,
    borderRadius: '8px',
    fontSize: defaultTheme.font.size[props.size],
    fontWeight: props.clicked ? 'bold' : 'inherit',
    backgroundColor: getBgColor(props),
    ...(props.clicked || props.disabled ? {} : hoverOpacity),
  }),
);

const SelectButton: React.FC<ButtonProps> = props => {
  const {
    children,
    bgColor,
    size,
    clicked,
    width,
    height,
    disabled,
    outline,
    type,
    onClick,
  } = props;

  return (
    <StyledButton
      bgColor={bgColor}
      size={size}
      clicked={clicked}
      aria-selected={clicked}
      width={width}
      height={height}
      disabled={disabled}
      outline={outline}
      type={type}
      onClick={onClick}
    >
      {children}
    </StyledButton>
  );
};

export default SelectButton;
